/**
 * Read-only date/time info bar for the booked slot.
 *
 * Same-day slot → one row: 📅 date  🕐 start - end.
 * Multi-day slot → two rows (📅 date 🕐 time per endpoint), mirroring
 * legacy prod so the end date is visible and each date pairs with its
 * own time instead of a confusing date-range + time-range split.
 * Uses account date/time format preference via useDateFormat.
 */
import { useMemo } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Edit05, Calendar, Clock } from '@kula-ai/icons-core';
import { useDateFormat } from '@/hooks/useDateFormat';
import moment from 'moment-timezone';

interface DateTimeFieldProps {
  /** Pipe-separated ISO: "startISO|endISO" */
  value?: string;
  /** Toggle into edit mode */
  onEdit?: () => void;
}

const ICON_COLOR = 'var(--kula-colors-secondary-700)';

const InfoRow = ({ date, time }: { date: string; time: string }) => (
  <Flex align="center" gap="3" flexWrap="wrap">
    {date && (
      // Fixed min-width so the clock/time column lines up across the two
      // rows of a multi-day slot (dates can vary in rendered width).
      <Flex align="center" gap="1.5" minW="116px">
        <Calendar size={16} color={ICON_COLOR} />
        <Text
          fontSize="14px"
          lineHeight="20px"
          color="secondary-900"
          style={{ fontWeight: 450 }}
        >
          {date}
        </Text>
      </Flex>
    )}
    {time && (
      <Flex align="center" gap="1.5">
        <Clock size={16} color={ICON_COLOR} />
        <Text
          fontSize="14px"
          lineHeight="20px"
          color="secondary-900"
          style={{ fontWeight: 450 }}
        >
          {time}
        </Text>
      </Flex>
    )}
  </Flex>
);

export const DateTimeField = ({ value, onEdit }: DateTimeFieldProps) => {
  const { dateFormat, timeFormat } = useDateFormat();

  const [startIso, endIso] = useMemo(() => {
    const val = typeof value === 'string' ? value : '';
    const parts = val.split('|');
    return [parts[0] || '', parts[1] || ''];
  }, [value]);

  if (!startIso && !endIso) return null;

  const dateFmt = dateFormat?.label || 'MM/DD/YYYY';
  const startDateStr = startIso ? moment(startIso).format(dateFmt) : '';
  const endDateStr = endIso ? moment(endIso).format(dateFmt) : '';
  const startTimeStr = startIso ? moment(startIso).format(timeFormat) : '';
  const endTimeStr = endIso ? moment(endIso).format(timeFormat) : '';

  // Multi-day = end falls on a different calendar day than start.
  const isMultiDay =
    !!startIso && !!endIso && !moment(startIso).isSame(moment(endIso), 'day');

  return (
    <Flex
      align="center"
      justify="space-between"
      w="100%"
      py="1.5"
      px="17px"
      mt="-8px"
      bg="secondary-25"
      borderRadius="md"
    >
      {isMultiDay ? (
        <Flex flexDir="column" gap="1.5">
          <InfoRow date={startDateStr} time={startTimeStr} />
          <InfoRow date={endDateStr} time={endTimeStr} />
        </Flex>
      ) : (
        <InfoRow
          date={startDateStr}
          time={
            startTimeStr && endTimeStr
              ? `${startTimeStr} - ${endTimeStr}`
              : startTimeStr || endTimeStr
          }
        />
      )}
      {onEdit && (
        <Flex
          as="button"
          type="button"
          align="center"
          justify="center"
          w="7"
          h="7"
          flexShrink={0}
          borderRadius="md"
          cursor="pointer"
          onClick={onEdit}
          aria-label="Edit date and time"
        >
          <Edit05 size={16} color={ICON_COLOR} />
        </Flex>
      )}
    </Flex>
  );
};
